import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { apiInstance, gql } from "@/services/httpAdapter";
import { errorLogger } from "@Common/helpers/logger";

interface IMetricsObject {
	metricName: string;
	metricValue: number;
}

interface IReferenceMetricsObject {
	_id: string;
	referenceName: string;
	referenceType: string;
	metrics: IMetricsObject[];
}

interface UserBasics {
	_id: string;
	fullName: string;
	username: string;
}

interface IEmployeeMetric {
	_id: string;
	user: UserBasics;
	metrics: IMetricsObject[];
}

interface IOEMCsiDefinition {
	_id: string;
	locationId: string;
	commissionCycleId: string;
	employeeMetrics: IEmployeeMetric;
	referenceMetrics: IReferenceMetricsObject;
	lastModifiedOn: string;
}

export async function getMyOEMVolvoCsi(locationId: string, commissionCycleId: string, userId: string) {
	const query = gql`
		query getMyOEMVolvoCsi($locationId: String!, $commissionCycleId: String!, $userId: String!) {
			getMyOEMVolvoCsi(locationId: $locationId, commissionCycleId: $commissionCycleId, userId: $userId) {
				_id
				locationId
				commissionCycleId
				employeeMetrics {
					_id
					user {
						_id
						fullName
						username
					}
					metrics {
						metricName
						metricValue
					}
				}
				referenceMetrics {
					_id
					referenceName
					referenceType
					metrics {
						metricName
						metricValue
					}
				}
				lastModifiedOn
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		userId,
	};

	try {
		const response: GraphqlQueryResponse<"getMyOEMVolvoCsi", IOEMCsiDefinition> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getMyOEMVolvoCsi;
	} catch (err) {
		errorLogger(err as Error, { function: "getMyOEMVolvoCsi", message: "Failed to get MyOEM Volvo CSI", variables });
		return null;
	}
}

export async function getMyOEMMitsubishiCsi(locationId: string, commissionCycleId: string, userId: string) {
	const query = gql`
		query getMyOEMMitsubishiCsi($locationId: String!, $commissionCycleId: String!, $userId: String!) {
			getMyOEMMitsubishiCsi(locationId: $locationId, commissionCycleId: $commissionCycleId, userId: $userId) {
				_id
				locationId
				commissionCycleId
				employeeMetrics {
					_id
					user {
						_id
						fullName
						username
					}
					metrics {
						metricName
						metricValue
					}
				}
				referenceMetrics {
					_id
					referenceName
					referenceType
					metrics {
						metricName
						metricValue
					}
				}
				lastModifiedOn
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		userId,
	};

	try {
		const response: GraphqlQueryResponse<"getMyOEMMitsubishiCsi", IOEMCsiDefinition> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getMyOEMMitsubishiCsi;
	} catch (err) {
		errorLogger(err as Error, { function: "getMyOEMMitsubishiCsi", message: "Failed to get MyOEM Mitsubishi CSI", variables });
		return null;
	}
}

export async function getMyOEMChevroletCsi(locationId: string, commissionCycleId: string, userId: string) {
	const query = gql`
		query getMyOEMChevroletCsi($locationId: String!, $commissionCycleId: String!, $userId: String!) {
			getMyOEMChevroletCsi(locationId: $locationId, commissionCycleId: $commissionCycleId, userId: $userId) {
				_id
				commissionCycleId
				employeeMetrics {
					_id
					user {
						_id
						fullName
						username
					}
					responses
					responseRate
					metrics {
						metricName
						metricValue
					}
				}
				referenceMetrics {
					_id
					referenceName
					referenceType
					metrics {
						metricName
						metricValue
					}
				}
				lastModifiedOn
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		userId,
	};

	try {
		const response: GraphqlQueryResponse<"getMyOEMChevroletCsi", IOEMCsiDefinition> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getMyOEMChevroletCsi;
	} catch (err) {
		errorLogger(err as Error, { function: "getMyOEMChevroletCsi", message: "Failed to get MyOEM Chevrolet CSI", variables });
		return null;
	}
}
